import { Fragment, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router';
import { useTranslation } from 'react-i18next';
import { AlertTriangle, ChevronDown, ChevronRight, Loader2, Plane, Search } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { EmptyState } from '@/components/ui/empty-state';
import { Skeleton } from '@/components/ui/skeleton';
import { cn } from '@/lib/cn';
import { InfiniteScrollSentinel } from '@/lib/pagination/infinite-scroll-sentinel';
import {
  useDisruption,
  useDisruptionRoster,
  useRebookingDetail,
} from '@/lib/disruptions/use-disruptions';
import {
  type DisruptionPnrGroupRow,
  type DisruptionRosterFilters,
  type RebookingGroupResult,
  type RosterPriorityTag,
  type RosterRebookingStatus,
  type RosterSort,
} from '@/lib/disruptions/types';
import { formatFlightDateTime } from '@/lib/disruptions/airport-tz';
import { RosterPnrDrawer } from './roster-pnr-drawer';
import { PnrRow } from '@/components/pnr/pnr-roster-row';
import { RosterFilterChips } from './roster-filter-chips';
import { RosterActionSidebar } from './roster-action-sidebar';
import { RosterMetricTiles } from './roster-metric-tiles';

// Disruption control-center roster tab (S12 / NEX-287). The roster is GROUPED BY
// PNR — one row per PNR group (the booking/family unit), which is the operator's
// actionable unit. Each group row shows PNR · flight · pax count + breakdown ·
// priority chips (union) · rebooking status; expanding the row reveals the member
// passengers. Server-filtered (priority chip → `priority`, rebooking-status select
// → `rebookingStatus`, search box → `q`) at the GROUP level + cursor-paginated via
// infinite scroll (frontend-conventions §7.5.1): the table body is its OWN bounded
// scroll region (§18.1 — no page scroll) with the sentinel inside it driving
// `fetchNextPage`. A family is never split across pages (upstream keysets by sub-case).
//
// Friendly labels are primary (PNR locator, flightLabel, leadName, rebooking status);
// URNs are de-emphasized secondary identifiers. Clicking the PNR opens the
// PNR-group action drawer (select option / override / exclude — S8/NEX-283).

interface RosterTabProps {
  disruptionUrn: string;
  /**
   * Optional starting filters (the Tower's queues / pools / flight strips deep-link
   * into a pre-filtered roster). Read ONCE as the initial state — the chips stay
   * the source of truth afterwards; remount (key) to apply a new set. Absent =
   * exactly the classic behaviour (no filters).
   */
  initialFilters?: {
    rebookingStatus?: RosterRebookingStatus | undefined;
    priority?: RosterPriorityTag | undefined;
    flightUrn?: string | undefined;
  };
  /**
   * `compact` (Tower): the metric tiles + the action sidebar are OWNED by the
   * Tower's instruments + pinned console, so the roster renders only the toolbar +
   * grid. Default false = exactly the classic layout.
   *
   * That hand-off used to LOSE something. The sidebar is the only place the
   * classic view can accept already-generated options in bulk without a fresh
   * vendor search (`AutomaticAcceptancePanel` → `auto-rebook@v2`), and the Tower
   * never re-provided it — so on the DEFAULT view an operator staring at 95
   * groups at OPTIONS_READY (IB376/BCN) had no way to take their computed
   * "recommended #1" short of "Rebook again", which re-runs the whole search.
   * Closed by auto-accept-rebooking / U2 (NEX-535): the Tower console now mounts
   * that SAME panel itself (`tower/tower-console.tsx` → TAKE THE RECOMMENDATION),
   * so the ownership claim above is now true in both directions. The gate below
   * stays as it is on purpose — the fix belongs in the owner, not in a second
   * sidebar the Tower would then have to lay out.
   */
  compact?: boolean;
}

export function RosterTab({ disruptionUrn, initialFilters, compact = false }: RosterTabProps) {
  const { t } = useTranslation('roster');
  const [priority, setPriority] = useState<RosterPriorityTag | null>(
    initialFilters?.priority ?? null,
  );
  const [rebookingStatus, setRebookingStatus] = useState<RosterRebookingStatus | null>(
    initialFilters?.rebookingStatus ?? null,
  );
  const [sortBy, setSortBy] = useState<RosterSort>('default');
  const [flightFilter, setFlightFilter] = useState<string | null>(
    initialFilters?.flightUrn ?? null,
  );
  const [collapsedFlights, setCollapsedFlights] = useState<Set<string>>(new Set());
  // The search box is debounced into the server filter so each keystroke doesn't
  // start a fresh cursor walk; the raw value drives the controlled input.
  const [rawQuery, setRawQuery] = useState('');
  const query = useDebounced(rawQuery, 300);
  const [drawerGroup, setDrawerGroup] = useState<DisruptionPnrGroupRow | null>(null);

  const filters: DisruptionRosterFilters = useMemo(
    () => ({
      ...(priority ? { priority } : {}),
      ...(rebookingStatus ? { rebookingStatus } : {}),
      ...(query.trim() ? { q: query.trim() } : {}),
      ...(flightFilter ? { flightUrn: flightFilter } : {}),
      ...(sortBy !== 'default' ? { sort: sortBy } : {}),
    }),
    [priority, rebookingStatus, query, flightFilter, sortBy],
  );

  const navigate = useNavigate();

  const { items, isLoading, isError, hasNextPage, isFetchingNextPage, fetchNextPage, refetch } =
    useDisruptionRoster(disruptionUrn, filters);

  // The drawer follows the LIVE row. The click only remembers WHICH group is open;
  // the facts come from the roster query, which every in-drawer write invalidates
  // (a stay-home declaration, a hotel hold, an election) — so the card that made
  // the change repaints from the truth instead of the snapshot taken at the click.
  // The snapshot stays as the fallback while the row is filtered/paged out.
  const liveDrawerGroup = useMemo(
    () =>
      drawerGroup
        ? (items.find((g) => g.subCaseUrn === drawerGroup.subCaseUrn) ?? drawerGroup)
        : null,
    [items, drawerGroup],
  );

  // SUGGESTED FLIGHT join (U1): the rebooking-detail read carries every group's
  // rebooking outcome (best option + strategies) at the disruption grain. We index
  // it by `groupId` and join into each roster row by `groupId === row.subCaseUrn`,
  // so the SUGGESTED FLIGHT column shows the winning flight without the row needing
  // to carry it. Live: this cache is refreshed over the WS progress feed (settle
  // outcomes fold into it via applyEvent), so the column fills in as a run settles.
  const { data: rebookingDetail } = useRebookingDetail(disruptionUrn);
  const suggestedByGroup = useMemo(() => {
    const map = new Map<string, RebookingGroupResult>();
    for (const g of rebookingDetail?.groups ?? []) map.set(g.groupId, g);
    return map;
  }, [rebookingDetail]);

  // Flight-filter options come from the disruption's OWN flights (its child cases,
  // already loaded by the detail page — this just reads the warm cache), NOT the
  // loaded roster pages: the dropdown must list every disrupted flight, and the
  // filter narrows the roster SERVER-side (flightUrn → indexed $match), so it pages
  // correctly over the whole flight even under infinite scroll. Ordered by departure.
  const { data: detail } = useDisruption(disruptionUrn);
  const flightOptions = useMemo(() => {
    const seen = new Map<string, { urn: string; label: string; departureAt: string }>();
    for (const c of detail?.cases ?? []) {
      if (!c.flightUrn || seen.has(c.flightUrn)) continue;
      const route = c.origin && c.destination ? `${c.origin}→${c.destination}` : '';
      const label = [c.flightNumber, route].filter(Boolean).join(' ') || c.flightUrn;
      seen.set(c.flightUrn, { urn: c.flightUrn, label, departureAt: c.departureAt ?? '' });
    }
    return [...seen.values()].sort(
      (a, b) => a.departureAt.localeCompare(b.departureAt) || a.label.localeCompare(b.label),
    );
  }, [detail]);

  // The server already orders the stream `(flightUrn, <sort>, urn)`, so grouping is
  // a pure client-side fold that PRESERVES that order: rows arrive flight-contiguous
  // and already sorted within each flight, so the headers + within-flight order are
  // correct AND complete under infinite scroll. A single-flight disruption — or a
  // flight-filtered view — yields one group and renders flat (no flight header).
  const flightGroups = useMemo(() => groupByFlight(items), [items]);
  const multiFlight = flightGroups.length > 1;

  const toggleFlight = (urn: string) =>
    setCollapsedFlights((prev) => {
      const next = new Set(prev);
      if (next.has(urn)) next.delete(urn);
      else next.add(urn);
      return next;
    });

  const hasFilters = !!priority || !!rebookingStatus || !!flightFilter || query.trim() !== '';

  return (
    // Scroll model (§18.1): on every viewport that can actually fit this tab —
    // tablet and up, verified — nothing scrolls here; the roster and the action
    // panel each scroll inside their own bounded box and this container is inert.
    // `overflow-y-auto` is the phone-only safety valve: at 375px the fixed chrome
    // above the data (header + tiles + toolbar) leaves the grid < 30px, and with a
    // hard `overflow-hidden` the roster clipped to height 0 — present in the DOM,
    // invisible to the operator. Paired with the grid's `minmax()` floors below,
    // the roster keeps a usable height and this ONE region scrolls instead. It is
    // still a contained scroll inside the tab, never the document.
    <div className="density-page flex h-full min-h-0 flex-col gap-3 overflow-y-auto overflow-x-hidden p-3 sm:gap-4 sm:p-6">
      {/* MVP header tiles (U1): affected flights / passengers / connections at risk
          / estimated cost. The cost tile drills into the existing cost-breakdown
          read (the Cost tab, addressed by the #cost hash). Live over the WS.
          `shrink-0`: a summary bar is fixed chrome (§18.1) — it must not be
          squeezed to make room for the data region below it. */}
      {compact ? null : (
        <div className="shrink-0">
          <RosterMetricTiles
            disruptionUrn={disruptionUrn}
            onCostClick={() => navigate({ hash: '#cost' }, { replace: true })}
          />
        </div>
      )}

      {/* Toolbar: PNR/flight/name search + flight + sort on one row; the priority +
          rebooking-status filters are now the rich chip row below (U2 — replaces the
          two dropdowns, keeping the SAME server `priority` / `rebookingStatus` params). */}
      <div className="flex shrink-0 flex-col gap-2" data-testid="roster-toolbar">
        <div className="flex flex-wrap items-center gap-3">
          {/* `flex-1 basis-48` (not `w-full`): a 100%-wide flex item forces its own
              row, which pushed Sort onto a second line on tablet and cost the data
              region ~40px of height for nothing. Now search + flight + sort share
              one row wherever they fit and wrap only when they genuinely can't. */}
          <div className="relative min-w-0 max-w-xs flex-1 basis-48">
            <Search
              className="absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground"
              aria-hidden
            />
            <input
              value={rawQuery}
              onChange={(e) => setRawQuery(e.target.value)}
              placeholder={t('toolbar.searchPlaceholder')}
              aria-label={t('toolbar.searchLabel')}
              className="w-full rounded-md border border-input bg-background py-1.5 pl-8 pr-2 text-sm"
            />
          </div>
          {flightOptions.length > 1 ? (
            <FlightFilter value={flightFilter} options={flightOptions} onChange={setFlightFilter} />
          ) : null}
          <SortControl value={sortBy} onChange={setSortBy} />
        </div>
        <RosterFilterChips
          priority={priority}
          onPriorityChange={setPriority}
          rebookingStatus={rebookingStatus}
          onRebookingStatusChange={setRebookingStatus}
        />
      </div>

      {/* Roster table + action sidebar — a responsive two-region workspace, laid out
          as a GRID rather than a flex box (NEX-299 / S25).

          Why the grid: as flex children in a column, the action sidebar is
          `shrink-0` around ~540-750px of natural content while the roster Card is
          `flex-1` (basis 0). Below `lg` the sidebar therefore ate the whole track
          and the roster collapsed to clientHeight **0** — the passenger table was
          literally invisible at 375px and 768px, and the sidebar's own bottom fell
          ~1300px past the frame with nothing to scroll it into view (the app-shell
          <main> is bounded, so it was clipped, not scrollable). Explicit
          `minmax(0, Nfr)` tracks make both regions bounded and non-collapsing:
          3fr roster / 2fr actions stacked on phone+tablet, and a `1fr | 20rem`
          two-column split from `lg` up. `minmax(0, …)` (not plain `fr`) is what
          stops a min-content blowout from re-growing the track.

          Each region keeps its OWN contained scroll (§18.1) — the page never
          scrolls, and scrolling the roster never moves the actions. */}
      <div
        className={cn(
          'grid min-h-[19rem] flex-1 gap-4 overflow-hidden lg:min-h-0',
          compact
            ? 'grid-rows-1'
            : 'grid-rows-[minmax(11rem,3fr)_minmax(7rem,2fr)] lg:grid-cols-[minmax(0,1fr)_20rem] lg:grid-rows-1',
        )}
      >
        <Card className="flex min-h-0 flex-col overflow-hidden">
          {/* The leaf data region: scrolls on BOTH axes. Vertically it walks the
            roster (driving `fetchNextPage`); horizontally it lets the 5-column
            table keep readable columns on a phone instead of being crushed or
            clipped — the horizontal scroll is contained here, never on the page. */}
          <CardContent className="min-h-0 flex-1 overflow-auto p-0">
            {isLoading ? (
              <div className="space-y-2 p-4">
                <Skeleton className="h-9 w-full" />
                <Skeleton className="h-9 w-full" />
                <Skeleton className="h-9 w-full" />
              </div>
            ) : isError ? (
              // Error is its own state, not an empty list: say what failed and offer
              // the one action that can fix it (re-run this filter's cursor walk).
              <div className="p-6" data-testid="roster-error">
                <EmptyState
                  icon={<AlertTriangle className="h-6 w-6" aria-hidden />}
                  title={t('error.title')}
                  description={t('error.description')}
                  action={
                    <Button onClick={() => refetch()} variant="outline">
                      {t('actions.retry')}
                    </Button>
                  }
                />
              </div>
            ) : items.length === 0 ? (
              <div className="p-6">
                <EmptyState
                  title={hasFilters ? t('empty.filteredTitle') : t('empty.title')}
                  description={
                    hasFilters ? t('empty.filteredDescription') : t('empty.description')
                  }
                />
              </div>
            ) : (
              // `min-w-[46rem]`: below that the five columns crush into unreadable
              // wraps, so the table keeps its width and the leaf region scrolls
              // sideways instead (§18.2 — reflow, never illegible).
              <table className="density-table w-full min-w-[46rem] text-sm" data-testid="roster-table">
                <thead className="sticky top-0 z-10 border-b border-border bg-background text-xs uppercase text-muted-foreground">
                  <tr>
                    <th className="px-3 py-2 text-left">{t('table.columns.pnr')}</th>
                    <th className="px-3 py-2 text-left">{t('table.columns.flight')}</th>
                    <th className="px-3 py-2 text-left">{t('table.columns.suggestedFlight')}</th>
                    <th className="px-3 py-2 text-left">{t('table.columns.priority')}</th>
                    <th className="px-3 py-2 text-left">{t('table.columns.rebooking')}</th>
                  </tr>
                </thead>
                <tbody>
                  {multiFlight
                    ? flightGroups.map((f) => {
                        const collapsed = collapsedFlights.has(f.flightUrn);
                        return (
                          <Fragment key={f.flightUrn}>
                            <FlightGroupHeader
                              flight={f}
                              collapsed={collapsed}
                              onToggle={() => toggleFlight(f.flightUrn)}
                            />
                            {collapsed
                              ? null
                              : f.pnrs.map((group) => (
                                  <PnrRow
                                    key={group.subCaseUrn}
                                    group={group}
                                    onOpen={() => setDrawerGroup(group)}
                                    showSuggested
                                    suggested={suggestedByGroup.get(group.subCaseUrn)}
                                  />
                                ))}
                          </Fragment>
                        );
                      })
                    : (flightGroups[0]?.pnrs ?? []).map((group) => (
                        <PnrRow
                          key={group.subCaseUrn}
                          group={group}
                          onOpen={() => setDrawerGroup(group)}
                          showSuggested
                          suggested={suggestedByGroup.get(group.subCaseUrn)}
                        />
                      ))}
                </tbody>
              </table>
            )}

            {/* Loading-more + sentinel live INSIDE the bounded scroll region (§18.1)
              so they cross into view as the operator scrolls, driving fetchNextPage. */}
            {isFetchingNextPage ? (
              <div
                className="flex items-center justify-center gap-2 p-3 text-xs text-muted-foreground"
                data-testid="roster-loading-more"
              >
                <Loader2 className="h-3 w-3 animate-spin" aria-hidden />
                {t('table.loadingMore')}
              </div>
            ) : null}
            <InfiniteScrollSentinel
              onReachEnd={fetchNextPage}
              disabled={!hasNextPage || isFetchingNextPage}
            />
          </CardContent>
        </Card>

        {/* Actions region. `h-full` on the <aside> is what makes its own
            `overflow-y-auto` engage: the grid track gives a definite height, so
            the panel scrolls inside its box instead of growing past the frame.

            `compact` (Tower) still drops it — see the prop's doc comment: the
            Tower console re-provides the bulk-accept from its own NOW section
            (auto-accept-rebooking / U2), so nothing is lost by not rendering a
            second sidebar here. */}
        {compact ? null : (
          <div className="min-h-0 overflow-hidden [&>aside]:h-full">
            <RosterActionSidebar disruptionUrn={disruptionUrn} />
          </div>
        )}
      </div>

      <RosterPnrDrawer
        disruptionUrn={disruptionUrn}
        group={liveDrawerGroup}
        onClose={() => setDrawerGroup(null)}
        // ↑ ↓ walk the loaded roster in its order; the card follows the live rows.
        navigation={{ groups: items, onNavigate: setDrawerGroup }}
      />
    </div>
  );
}

// Sort options for the combobox, in display order. The order itself is applied
// SERVER-side (compound keyset over `(flightUrn, <sort>, urn)`) — the UI only
// names the criteria, and the names live in i18n (`roster:toolbar.sortOption.*`).
const SORT_OPTIONS: readonly RosterSort[] = [
  'default',
  'pnr',
  'flight',
  'status',
  'pax',
  'needs',
];

interface FlightGroup {
  flightUrn: string;
  flightLabel: string;
  flightNumber: string;
  origin: string;
  destination: string;
  departureAt: string;
  pnrs: DisruptionPnrGroupRow[];
}

/** Fold the server-ordered roster stream into flight groups, PRESERVING order: the
 *  stream already arrives `(flightUrn, <sort>, urn)`-ordered, so rows of a flight
 *  are contiguous + already sorted within. Flights appear in first-seen order (=
 *  the server's flightUrn order). One group ⇒ the caller renders flat (no header). */
function groupByFlight(items: DisruptionPnrGroupRow[]): FlightGroup[] {
  const byFlight = new Map<string, FlightGroup>();
  const order: string[] = [];
  for (const g of items) {
    const key = g.flightUrn || g.flightLabel || '—';
    let fg = byFlight.get(key);
    if (!fg) {
      fg = {
        flightUrn: key,
        flightLabel: g.flightLabel,
        flightNumber: g.flightNumber,
        origin: g.origin,
        destination: g.destination,
        departureAt: g.departureAt,
        pnrs: [],
      };
      byFlight.set(key, fg);
      order.push(key);
    }
    fg.pnrs.push(g);
  }
  return order.map((k) => byFlight.get(k)!);
}

/** Flight-group header row — shown only when the disruption spans >1 flight. The
 *  whole header is a collapse toggle for its PNRs (the chevron mirrors the state). */
function FlightGroupHeader({
  flight,
  collapsed,
  onToggle,
}: {
  flight: FlightGroup;
  collapsed: boolean;
  onToggle: () => void;
}) {
  const { t } = useTranslation('roster');
  const pax = flight.pnrs.reduce((s, g) => s + (g.paxCount ?? 0), 0);
  return (
    <tr className="border-b border-border bg-muted/60" data-testid="roster-flight-header">
      <td colSpan={5} className="px-1 py-0">
        <button
          type="button"
          onClick={onToggle}
          aria-expanded={!collapsed}
          aria-label={collapsed ? t('flightHeader.expand') : t('flightHeader.collapse')}
          className="flex w-full flex-wrap items-center gap-2 px-2 py-2 text-left hover:bg-muted/40"
          data-testid="roster-flight-toggle"
        >
          {collapsed ? (
            <ChevronRight className="h-4 w-4 shrink-0 text-muted-foreground" aria-hidden />
          ) : (
            <ChevronDown className="h-4 w-4 shrink-0 text-muted-foreground" aria-hidden />
          )}
          <Plane className="h-4 w-4 text-muted-foreground" aria-hidden />
          <span className="font-semibold">{flight.flightNumber || flight.flightLabel}</span>
          {flight.origin && flight.destination ? (
            <span className="text-muted-foreground">
              {flight.origin}→{flight.destination}
            </span>
          ) : null}
          {flight.departureAt ? (
            <span className="text-xs text-muted-foreground">
              {formatFlightDateTime(flight.departureAt, flight.origin)}
            </span>
          ) : null}
          <Badge variant="muted" size="sm">
            {t('flightHeader.summary', { count: flight.pnrs.length, pax })}
          </Badge>
        </button>
      </td>
    </tr>
  );
}

/** Flight filter as a combobox next to the search — narrows the roster to one
 *  disrupted flight SERVER-side (`flightUrn` → indexed $match in cases), so it pages
 *  correctly over the whole flight under infinite scroll. Options are the
 *  disruption's own flights (child cases); shown only when it spans >1 flight. */
function FlightFilter({
  value,
  options,
  onChange,
}: {
  value: string | null;
  options: Array<{ urn: string; label: string }>;
  onChange: (v: string | null) => void;
}) {
  const { t } = useTranslation('roster');
  return (
    <label className="flex items-center gap-2 text-sm text-muted-foreground">
      {t('toolbar.flightLabel')}
      <select
        value={value ?? ''}
        onChange={(e) => onChange(e.target.value || null)}
        aria-label={t('toolbar.flightFilterLabel')}
        className="rounded-md border border-input bg-background py-1.5 pl-2 pr-7 text-sm text-foreground"
        data-testid="roster-flight-filter"
      >
        <option value="">{t('toolbar.allFlights')}</option>
        {options.map((o) => (
          <option key={o.urn} value={o.urn}>
            {o.label}
          </option>
        ))}
      </select>
    </label>
  );
}

/** Sort combobox next to the search (client-side over the loaded roster). */
function SortControl({
  value,
  onChange,
}: {
  value: RosterSort;
  onChange: (v: RosterSort) => void;
}) {
  const { t } = useTranslation('roster');
  return (
    <label className="flex items-center gap-2 text-sm text-muted-foreground">
      {t('toolbar.sortLabel')}
      <select
        value={value}
        onChange={(e) => onChange(e.target.value as RosterSort)}
        aria-label={t('toolbar.sortAriaLabel')}
        className="rounded-md border border-input bg-background py-1.5 pl-2 pr-7 text-sm text-foreground"
        data-testid="roster-sort"
      >
        {SORT_OPTIONS.map((k) => (
          <option key={k} value={k}>
            {t(`toolbar.sortOption.${k}`)}
          </option>
        ))}
      </select>
    </label>
  );
}

// Tiny debounce so search keystrokes coalesce into one server filter change
// (each change restarts the cursor walk). No external dep — local effect.
function useDebounced<T>(value: T, ms: number): T {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setDebounced(value), ms);
    return () => clearTimeout(t);
  }, [value, ms]);
  return debounced;
}
